import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Button, Radio, RadioGroup } from '@structural-climbing/ui'
import { Sheet } from '../../design/Sheet'
import { formatPrice } from './price'
import type { PayablePlan } from './types'

interface Props {
  isOpen: boolean
  onClose: () => void
  gymId: string
  plans: PayablePlan[]
  /**
   * Called when the Worker refuses with a code, which always means the list the
   * member is looking at is out of date: the gym turned payments off, the plan
   * stopped being for sale, or a plan was granted at the desk a moment ago.
   * The caller refetches, which is what takes the button away.
   */
  onRefused?: () => void
}

// Server refusal codes to the one sentence the member can act on. Anything not
// listed here is a bug on our side, not a situation to explain.
const ERROR_KEYS: Record<string, string> = {
  GYM_BLOCKED: 'payments.errorPaymentsOff',
  PAYMENTS_OFF: 'payments.errorPaymentsOff',
  PLAN_NOT_PAYABLE: 'payments.errorPlanGone',
  SUBSCRIPTION_ALREADY_ACTIVE: 'payments.errorAlreadyActive',
}

/**
 * Pick a plan, pay by card.
 *
 * The sheet only ever shows the price the member pays. What Stripe charges the
 * gym and what we take as a platform fee are the gym's business, and a member
 * reading that split would be left with a question the gym has to field.
 *
 * The money never passes through us: the Checkout Session opens on the gym's
 * own Stripe account, so their name is on the statement and the refund is
 * theirs to issue.
 */
export function PayPlanSheet({ isOpen, onClose, gymId, plans, onRefused }: Props) {
  const { getAccessTokenSilently } = useAuth()
  const { t, i18n } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [selected, setSelected] = useState<string>(plans[0]?.id ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const plan = plans.find((p) => p.id === selected) ?? plans[0]

  /** What the plan buys, in the one line under its name. */
  function planDetail(p: PayablePlan): string {
    if (p.plan_type === 'day_pass') return t('payments.planDayPass')
    if (p.max_entries !== null) return t('payments.planEntries', { count: p.max_entries })
    return t('payments.planDays', { count: p.duration_days })
  }

  async function pay() {
    if (!plan) return
    setBusy(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/my-checkout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ gym_id: gymId, gym_plan_id: plan.id }),
      })
      const data = (await res.json().catch(() => ({}))) as { url?: string; code?: string }
      if (res.ok && data.url) {
        // Stripe's own hosted page, on a different origin: a full navigation,
        // not a router push. Deliberately leaves `busy` set, because the button
        // must not come back to life while the browser is still leaving.
        window.location.assign(data.url)
        return
      }
      if (data.code) onRefused?.()
      setError(t(ERROR_KEYS[data.code ?? ''] ?? 'payments.errorGeneric'))
    } catch {
      setError(t('payments.errorGeneric'))
    }
    setBusy(false)
  }

  return (
    <Sheet
      isOpen={isOpen}
      // Closing mid-request would leave a Checkout Session opening into
      // nothing, so the sheet stays put until the attempt resolves.
      onClose={busy ? () => {} : onClose}
      aria-label={t('payments.sheetTitle')}
      data-testid="pay-plan-sheet"
    >
      <div className="flex flex-col gap-4 overflow-y-auto px-6 pb-6 pt-1">
        <div>
          <p className="text-base font-semibold">{t('payments.sheetTitle')}</p>
          <p className="mt-1 text-sm text-foreground/55">{t('payments.sheetIntro')}</p>
        </div>

        <RadioGroup
          value={selected}
          onValueChange={setSelected}
          aria-label={t('payments.choosePlan')}
        >
          {plans.map((p) => (
            <Radio key={p.id} value={p.id} description={planDetail(p)}>
              <span className="text-sm">
                {p.name}
                <span className="ml-2 font-semibold">
                  {formatPrice(p.price, p.currency, i18n.language)}
                </span>
              </span>
            </Radio>
          ))}
        </RadioGroup>

        {error && <p className="text-sm text-danger">{error}</p>}

        <Button
          className="w-full bg-[var(--gym-primary)] text-[var(--gym-on-primary)]"
          isLoading={busy}
          isDisabled={!plan}
          onClick={() => void pay()}
        >
          {plan
            ? t('payments.payButton', {
                price: formatPrice(plan.price, plan.currency, i18n.language),
              })
            : t('payments.sheetTitle')}
        </Button>

        <p className="text-xs text-foreground/40">{t('payments.stripeNote')}</p>
      </div>
    </Sheet>
  )
}
